import React, { useEffect, useRef } from 'react';

interface ParametricSphereArtTopoProps {
  width?: number;
  height?: number;
  dotSpacing?: number;
  rotationSpeed?: number;
  dotColor?: string;
  axisColor?: string;
  opacity?: number;
}

export const ParametricSphereArtTopo: React.FC<ParametricSphereArtTopoProps> = ({
  width = 400,
  height = 400,
  dotSpacing = 2.5,
  rotationSpeed = 0.003,
  dotColor = '#ffffff',
  axisColor = '#ffffff',
  opacity = 0.15
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const timeRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.style.opacity = opacity.toString();

    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) * 0.3;

    const rotatePoint = (p: { x: number; y: number; z: number }, time: number): { x: number; y: number; z: number } => {
      // Apply subtle initial tilt for better 3D perspective (15 degrees)
      const tiltAngle = Math.PI / 12;
      const cosTilt = Math.cos(tiltAngle);
      const sinTilt = Math.sin(tiltAngle);

      const xTilted = p.x * cosTilt - p.y * sinTilt;
      const yTilted = p.x * sinTilt + p.y * cosTilt;

      // Then rotate around Y axis for the spinning animation
      const cosY = Math.cos(time);
      const sinY = Math.sin(time);

      const x1 = xTilted * cosY - p.z * sinY;
      const z1 = xTilted * sinY + p.z * cosY;

      return { x: x1, y: yTilted, z: z1 };
    };

    const projectPoint = (p: { x: number; y: number; z: number }): { x: number; y: number } => {
      const scale = 1 + p.z / (radius * 2.5);
      return {
        x: centerX + p.x * scale,
        y: centerY + p.y * scale
      };
    };

    const drawDottedLine = (points: Array<{ x: number; y: number; z: number }>, time: number): void => {
      const rotatedPoints = points.map(p => rotatePoint(p, time));

      const sortedPoints = rotatedPoints
        .map((p, i) => ({ ...p, index: i }))
        .sort((a, b) => a.z - b.z);

      sortedPoints.forEach(p => {
        const screenPos = projectPoint(p);

        ctx.fillStyle = dotColor;
        ctx.beginPath();
        ctx.arc(screenPos.x, screenPos.y, 1.2, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const drawAxis = (startPoint: { x: number; y: number; z: number }, endPoint: { x: number; y: number; z: number }, time: number, opacity: number = 0.6): void => {
      const rotatedStart = rotatePoint(startPoint, time);
      const rotatedEnd = rotatePoint(endPoint, time);

      const projStart = projectPoint(rotatedStart);
      const projEnd = projectPoint(rotatedEnd);

      ctx.strokeStyle = axisColor;
      ctx.globalAlpha = opacity;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(projStart.x, projStart.y);
      ctx.lineTo(projEnd.x, projEnd.y);
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.setLineDash([]);
    };

    const generateTopoContours = (time: number): Array<Array<{ x: number; y: number; z: number }>> => {
      const contours: Array<Array<{ x: number; y: number; z: number }>> = [];
      const numContours = 16;
      const pointsPerContour = 120;

      for (let contourIdx = 0; contourIdx < numContours; contourIdx++) {
        const contourRadius = (contourIdx / numContours) * radius;
        const contour: Array<{ x: number; y: number; z: number }> = [];

        for (let i = 0; i < pointsPerContour; i++) {
          const angle = (i / pointsPerContour) * Math.PI * 2;
          
          // Pulsing amplitude - expands and contracts smoothly (increased speed and range)
          const pulseAmount = Math.sin(time * 1.2 + contourIdx * 0.25) * 0.5 + 0.5;
          const pulseScale = 0.6 + pulseAmount * 0.8; // Ranges from 0.6 to 1.4 (increased range)
          const waveAmplitude = (0.12 + 0.1 * pulseAmount) * Math.sin(contourIdx * 0.5); // Increased amplitude
          
          // Back and forth phase animations - waves move in opposite directions (faster)
          const phaseShift1 = time * 0.6;
          const phaseShift2 = time * -0.5; // Opposite direction
          const phaseShift3 = time * 0.8;
          
          const wave1 = Math.sin(angle * 3 + phaseShift1) * waveAmplitude;
          const wave2 = Math.cos(angle * 2.3 + phaseShift2) * waveAmplitude * 0.6;
          const wave3 = Math.sin(angle * 5.2 + contourIdx * 0.3 + phaseShift3) * waveAmplitude * 0.4;
          
          // Radial distortion with pulsing effect (increased)
          const radialWarp = Math.sin(time * 0.8 + contourIdx * 0.15) * 0.12 * pulseAmount;
          const adjustedRadius = contourRadius * pulseScale * (1 + wave1 + wave2 + wave3 + radialWarp);
          
          const x = adjustedRadius * Math.cos(angle);
          const z = adjustedRadius * Math.sin(angle);
          
          // Animated vertical displacement with pulsing (increased)
          const verticalWave = Math.sin(time * 0.7 + contourIdx * 0.1) * 0.2 * pulseAmount;
          const verticalPulse = Math.sin(time * 1.0 + contourIdx * 0.2) * 0.12;
          const y = (Math.sin(contourIdx / numContours * Math.PI - Math.PI / 2) * radius * 0.9) + (verticalWave + verticalPulse) * radius;

          contour.push({ x, y, z });
        }
        contours.push(contour);
      }

      return contours;
    };

    const generateOrbitalCurves = (): Array<Array<{ x: number; y: number; z: number }>> => {
      const curves: Array<Array<{ x: number; y: number; z: number }>> = [];
      const numCurves = 6;
      const pointsPerCurve = 150;

      for (let curveIdx = 0; curveIdx < numCurves; curveIdx++) {
        const inclination = (curveIdx / numCurves) * Math.PI * 0.6;
        const curve: Array<{ x: number; y: number; z: number }> = [];

        for (let i = 0; i < pointsPerCurve; i++) {
          const t = (i / pointsPerCurve) * Math.PI * 2;
          const r = radius * (0.7 + 0.3 * Math.sin(t * 2.5));
          
          const x = r * Math.cos(t);
          const y = r * Math.sin(t) * Math.cos(inclination);
          const z = r * Math.sin(t) * Math.sin(inclination);

          curve.push({ x, y, z });
        }
        curves.push(curve);
      }

      return curves;
    };

    const orbitalCurves = generateOrbitalCurves();

    const animate = (): void => {
      ctx.clearRect(0, 0, width, height);

      timeRef.current += rotationSpeed;

      // Generate animated contours each frame
      const topoContours = generateTopoContours(timeRef.current);

      topoContours.forEach(contour => {
        drawDottedLine(contour, timeRef.current);
      });

      orbitalCurves.forEach(curve => {
        drawDottedLine(curve, timeRef.current);
      });

      const axisLength = radius * 1.3;
      drawAxis({ x: -axisLength, y: 0, z: 0 }, { x: axisLength, y: 0, z: 0 }, timeRef.current, 0.5);
      drawAxis({ x: 0, y: -axisLength, z: 0 }, { x: 0, y: axisLength, z: 0 }, timeRef.current, 0.6);
      drawAxis({ x: 0, y: 0, z: -axisLength }, { x: 0, y: 0, z: axisLength }, timeRef.current, 0.5);

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [width, height, dotSpacing, rotationSpeed, dotColor, axisColor, opacity]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="font-seurat"
      style={{
        display: 'block',
        width: '100%',
        height: '100%',
        maxWidth: '100vw',
        maxHeight: '100vh',
      }}
    />
  );
};
